import { useState, useEffect, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Gift, X, Flame, Coins, Sparkles, Calendar } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = "fruitCrush_token";

const getAuthHeader = () => {
  const token = localStorage.getItem(TOKEN_KEY);
  return token ? `Bearer ${token}` : "";
};

export function DailyRewardModal({ isOpen, onClose, onClaimed }) {
  const [status, setStatus] = useState(null);
  const [claiming, setClaiming] = useState(false);
  const [claimed, setClaimed] = useState(false);
  const [reward, setReward] = useState(null);

  useEffect(() => {
    if (isOpen) fetchStatus();
  }, [isOpen]);

  const fetchStatus = async () => {
    try {
      const res = await fetch(`${API_URL}/api/daily-reward/status`, {
        headers: { Authorization: getAuthHeader() }
      });
      const data = await res.json();
      setStatus(data);
      if (!data.available) setClaimed(true);
    } catch { /* silent */ }
  };

  const handleClaim = async () => {
    setClaiming(true);
    try {
      const res = await fetch(`${API_URL}/api/daily-reward/claim`, {
        method: "POST",
        headers: { Authorization: getAuthHeader() }
      });
      const data = await res.json();
      if (data.claimed) {
        setReward(data.reward);
        setClaimed(true);
        setStatus(prev => ({ ...prev, streak: data.streak, available: false }));
        onClaimed?.();
        toast.success(`+${data.reward.coins} coins claimed!`);
      } else if (data.already_claimed) {
        setClaimed(true);
      }
    } catch {
      toast.error("Failed to claim reward");
    } finally {
      setClaiming(false);
    }
  };

  // Streak preview (next 7 days)
  const streakDays = useMemo(() => {
    const currentStreak = status?.streak || 0;
    return Array.from({ length: 7 }, (_, i) => {
      const day = i + 1;
      const base = 50 + Math.min(day - 1, 6) * 25;
      const milestone = day === 7 ? 100 : 0;
      return {
        day,
        coins: base + milestone,
        isMilestone: day === 7,
        isPast: day <= currentStreak % 7 || (currentStreak % 7 === 0 && currentStreak > 0 && day <= 7),
        isCurrent: day === (currentStreak % 7) + 1 || (currentStreak % 7 === 0 && day === 1)
      };
    });
  }, [status?.streak]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" data-testid="daily-reward-modal">
      <div className="bg-slate-900 border border-white/10 rounded-2xl w-full max-w-sm overflow-hidden shadow-2xl">
        {/* Header */}
        <div className="bg-gradient-to-r from-amber-600 to-yellow-500 p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
              <Gift className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-white">Daily Reward</h2>
              {status && <p className="text-white/70 text-xs flex items-center gap-1"><Flame className="w-3 h-3" /> {status.streak} day streak</p>}
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-white/20 transition-colors" data-testid="daily-reward-close">
            <X className="w-5 h-5 text-white" />
          </button>
        </div>

        {/* Streak Calendar */}
        <div className="p-4">
          <div className="grid grid-cols-7 gap-1.5 mb-4">
            {streakDays.map((d) => (
              <div
                key={d.day}
                className={cn(
                  "flex flex-col items-center p-1.5 rounded-lg text-center transition-all",
                  d.isPast ? "bg-amber-500/20 border border-amber-400/30" : "",
                  d.isCurrent && !claimed ? "bg-amber-500/30 border-2 border-amber-400 ring-2 ring-amber-400/30" : "",
                  d.isCurrent && claimed ? "bg-green-500/20 border border-green-400/30" : "",
                  !d.isPast && !d.isCurrent ? "bg-slate-800 border border-slate-700" : ""
                )}
              >
                <span className="text-[10px] text-slate-400">Day {d.day}</span>
                <span className="text-xs font-bold text-yellow-400">🪙{d.coins}</span>
                {d.isMilestone && <Sparkles className="w-3 h-3 text-amber-400" />}
              </div>
            ))}
          </div>

          {/* Claim Button */}
          {!claimed ? (
            <div className="text-center space-y-3">
              <div className="bg-amber-500/10 rounded-xl p-4 border border-amber-500/20">
                <p className="text-amber-400 text-sm font-medium mb-1">Today's Reward</p>
                <p className="text-3xl font-black text-white">🪙 {status?.reward?.coins || 50}</p>
              </div>
              <Button
                onClick={handleClaim}
                disabled={claiming}
                className="w-full bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-600 hover:to-yellow-600 text-white font-bold text-lg py-6"
                data-testid="daily-reward-claim"
              >
                {claiming ? "Claiming..." : "Claim Reward!"}
              </Button>
            </div>
          ) : (
            <div className="text-center space-y-3">
              <div className="bg-green-500/10 rounded-xl p-4 border border-green-500/20">
                <p className="text-green-400 text-xl font-bold">Claimed!</p>
                <p className="text-white text-2xl font-black mt-1">🪙 {reward?.coins || status?.reward?.coins || 50}</p>
              </div>
              <p className="text-slate-400 text-xs">Come back tomorrow to keep your streak!</p>
              <Button onClick={onClose} variant="outline" className="w-full border-slate-600 text-slate-300">
                Continue Playing
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default DailyRewardModal;
